import React from "react";
import CssBaseline from "@mui/material/CssBaseline";
import {createTheme, ThemeProvider} from "@mui/material/styles";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers'

// Brand constants — single definition point for non-MUI consumers (Amplify auth
// theme, splash gradients) that can't read `theme.vars.*`. Import these instead
// of re-hardcoding the hex. Q1=Option A: #00A8E8 is the primary brand accent;
// the navy is now a dark-chrome surface (background.elevated), not a brand color.
export const BRAND_ACCENT = '#00A8E8'
export const BRAND_NAVY = '#0A1128'
export const SURFACE_PAPER = '#ffffff'
export const SURFACE_DEFAULT = '#f8fafc'

// TOKEN-ESCAPE: raw hex for non-MUI renderers (react-icons fills, chart libs)
// that can't take theme.vars refs. Mirrored into palette.feature below for sx
// access. This is the sanctioned escape hatch — see the entry.* tokens in the
// taxonomy for the MUI-side equivalent.
export const featureColors = {
  prompt: '#50627a',
  behavior: '#507a6f',
  organization: '#696B38',
  themes: '#59547a',
}

// Affective/domain palette extension. NAMES are palette roles only (no
// health/wellness/mood copy). entry.mood* are the issue's affective roles;
// entry.chat/chapter/story/shareTag are the domain accents the Entries list
// and sharing surfaces consume via theme.vars.palette.entry.*.
type EntryPalette = {
  moodPositive: string; moodNegative: string; neutral: string
  chat: string; chapter: string; story: string; shareTag: string
}
type FeaturePalette = { prompt: string; behavior: string; organization: string; themes: string }

declare module '@mui/material/styles' {
  interface Palette { feature: FeaturePalette; entry: EntryPalette }
  interface PaletteOptions { feature?: FeaturePalette; entry?: EntryPalette }
  interface TypeBackground { elevated: string }
  // The theme below sets `cssVariables`, so `theme.vars` always exists
  // at runtime. Without this augmentation MUI types `theme.vars` as optional
  // (`Partial<Pick<CssVarsTheme,'vars'>>`), which makes every `theme.vars.*`
  // callsite a TS18048 ("possibly undefined") error — blocking the tokenized
  // design-system remap that docs/gnothi/web.md directs new code toward.
  interface CssThemeVariables { enabled: true }
}

// Gradient button styles - splash/marketing pages only, not in-app
export const splashGradient = {
  primary: {
    background: 'linear-gradient(135deg, #0A1128 0%, #1a295c 100%)',
    boxShadow: '0 4px 14px 0 rgba(0,0,0,0.1)',
    '&:hover': {
      background: 'linear-gradient(135deg, #1a295c 0%, #0A1128 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
    },
  },
  secondary: {
    background: 'linear-gradient(135deg, #00A8E8 0%, #0077c2 100%)',
    boxShadow: '0 4px 14px 0 rgba(0,0,0,0.1)',
    '&:hover': {
      background: 'linear-gradient(135deg, #0077c2 0%, #00A8E8 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
    },
  },
} as const

export const theme = createTheme({
  // colorSchemeSelector:'class' is required for setMode() to work. Without it,
  // MUI defaults to 'media' (prefers-color-scheme) when both colorSchemes are
  // present, which ignores setMode() calls entirely. 'class' makes MUI toggle
  // a CSS class on <html>. MUI persists/restores the choice itself via
  // localStorage 'mui-mode' — AccountMenu reads/writes it through useColorScheme().
  cssVariables: { colorSchemeSelector: 'class' },
  colorSchemes: {
    light: {
      palette: {
        // Q1=Option A: BRAND_ACCENT (#00A8E8) is primary. secondary keeps the
        // SAME blue so the ~40 deferred `color="secondary"`/`secondary.main`
        // callsites stay blue until they're re-pointed to primary in a later
        // wave. Navy is no longer a brand slot — it's background.elevated.
        primary: { main: BRAND_ACCENT, light: '#38bdf8', contrastText: '#ffffff' },
        secondary: { main: BRAND_ACCENT, light: '#38bdf8', contrastText: '#ffffff' },
        background: { paper: SURFACE_PAPER, default: SURFACE_DEFAULT, elevated: BRAND_NAVY },
        text: { primary: '#0f172a', secondary: '#475569', disabled: '#94a3b8' },
        divider: 'rgba(226, 232, 240, 0.8)',
        error: { main: '#ef4444' },
        warning: { main: '#f59e0b' },
        success: { main: '#10b981' },
        feature: featureColors,
        entry: {
          moodPositive: '#10b981', moodNegative: '#ef4444', neutral: '#9E9E9E',
          chat: '#00897B', chapter: '#6A1B9A', story: '#6A1B9A', shareTag: '#7B1FA2',
        },
      },
    },
    dark: {
      palette: {
        primary: { main: '#38bdf8', light: '#7dd3fc', contrastText: '#0A1128' },
        secondary: { main: '#38bdf8', light: '#7dd3fc', contrastText: '#0A1128' },
        background: { paper: '#0f172a', default: '#0a0f1d', elevated: '#1e293b' },
        text: { primary: '#f1f5f9', secondary: '#94a3b8', disabled: '#64748b' },
        divider: 'rgba(148, 163, 184, 0.2)',
        error: { main: '#f87171' },
        warning: { main: '#fbbf24' },
        success: { main: '#34d399' },
        feature: featureColors,
        entry: {
          moodPositive: '#34d399', moodNegative: '#f87171', neutral: '#9E9E9E',
          chat: '#4db6ac', chapter: '#ba68c8', story: '#ba68c8', shareTag: '#ce93d8',
        },
      },
    },
  },
  typography: {
    fontFamily: 'Inter',
    h1: {
      fontWeight: 700,
      fontFamily: 'Outfit',
      fontSize: '4.5rem',
      lineHeight: '1.1',
      marginBottom: '1rem',
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 600,
      fontFamily: 'Outfit',
      fontSize: '3rem',
      lineHeight: '1.2',
      marginBottom: '1rem',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontFamily: 'Outfit',
      fontSize: '2rem',
      lineHeight: '1.3',
    },
    h4: {
      fontWeight: 400,
      fontFamily: 'Inter',
      fontSize: '1.5rem',
      lineHeight: '1.5',
      marginBottom: '1rem',
    },
    h5: {
      fontWeight: 500,
      fontFamily: 'Outfit',
      lineHeight: '1.3',
      fontSize: "1.5rem"
    },
    h6: {
      fontWeight: 600,
      fontFamily: 'Inter',
      lineHeight: '1.3',
      fontSize: '1.125rem',
    },
    subtitle1: {
      fontFamily: 'Inter',
      fontSize: '1.25rem',
      fontWeight: '400',
    },
    subtitle2: {
      fontFamily: 'Outfit',
      fontSize: '1.125rem',
      fontWeight: '500',
    },
    body1: {
      fontFamily: 'Inter',
      fontWeight: '400',
      fontSize: "1rem",
      lineHeight: '1.6',
    },
    body2: {
      fontFamily: 'Inter',
      fontWeight: '400',
      fontSize: "0.875rem",
      lineHeight: '1.6',
    },
    caption: {
      fontFamily: 'Inter',
    },
    overline: {
      fontFamily: 'Inter',
      fontWeight: '600',
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
    button: {
      fontFamily: "Inter",
      fontSize: '0.875rem',
      fontWeight: '500',
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        // Q1=Option A: default to the (now-primary) blue brand accent. Buttons
        // must keep rendering blue; this swap is atomic with the palette swap.
        color: 'primary' as const,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 4,
          transition: 'all 0.2s ease-in-out',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({theme}) => ({
          backgroundColor: theme.vars.palette.background.paper,
          borderRadius: 4,
          boxShadow: '0 4px 14px -4px rgba(0,0,0,0.06)',
          transition: 'all 0.3s ease-in-out',
          border: `1px solid ${theme.vars.palette.divider}`,
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: ({theme}) => ({
          backgroundColor: theme.vars.palette.background.paper,
          borderRadius: '4px !important',
        }),
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: ({theme}) => ({
          fontSize: '0.6875rem',
          fontWeight: 600,
          fontFamily: 'Inter',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: theme.vars.palette.text.disabled,
          lineHeight: '1.5',
          paddingTop: '16px',
          paddingBottom: '4px',
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small' as const,
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small' as const,
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        size: 'small' as const,
      },
    },
  },
})

export default function Mui({children}: React.PropsWithChildren<{}>) {
  return <LocalizationProvider dateAdapter={AdapterDayjs}>
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline />
      {children}
    </ThemeProvider>
  </LocalizationProvider>
}
